import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BTCDominanceData {
  id: string;
  date: string;
  btc_dominance: number;
  total_market_cap: number | null;
  total_volume_24h: number | null;
  active_cryptocurrencies: number | null;
  created_at: string | null;
}

export const useBTCDominanceHistory = (days: number = 90) => {
  return useQuery({
    queryKey: ['btc-dominance-history', days],
    queryFn: async (): Promise<BTCDominanceData[]> => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const { data, error } = await supabase
        .from('btc_dominance_history')
        .select('*')
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: true });
      
      if (error) {
        console.error('Error fetching BTC dominance history:', error);
        throw error;
      }
      
      return data as BTCDominanceData[];
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchInterval: 1000 * 60 * 60, // 1 hour
  });
};

export const useBTCDominanceLatest = () => {
  return useQuery({
    queryKey: ['btc-dominance-latest'],
    queryFn: async (): Promise<BTCDominanceData | null> => {
      const { data, error } = await supabase
        .from('btc_dominance_history')
        .select('*')
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching latest BTC dominance:', error);
        throw error;
      }
      
      return data as BTCDominanceData | null;
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
    refetchInterval: 1000 * 60 * 60, // 1 hour
  });
};

// Helper functions for UI
export const getDominanceLabel = (value: number): string => {
  if (value >= 60) return 'BTC Season';
  if (value >= 55) return 'BTC Dominante';
  if (value >= 45) return 'Neutral';
  if (value >= 40) return 'Alt Dominante';
  return 'Alt Season';
};

export const getDominanceColor = (value: number): string => {
  if (value >= 60) return 'hsl(38, 92%, 50%)'; // Bitcoin orange
  if (value >= 55) return 'hsl(38, 70%, 55%)';
  if (value >= 45) return 'hsl(220, 13%, 50%)'; // Neutral gray
  if (value >= 40) return 'hsl(280, 60%, 55%)'; // Alt purple
  return 'hsl(280, 70%, 50%)'; // Strong alt purple
};

export const getDominanceTrend = (current: number, previous: number): 'up' | 'down' | 'neutral' => {
  const diff = current - previous;
  if (diff > 0.5) return 'up';
  if (diff < -0.5) return 'down';
  return 'neutral';
};
